/**
 * Exponential Moving Average (EMA)
 *
 * Weighted moving average giving greater weight to recent prices.
 * Formula: EMA = price × k + EMA_prev × (1 − k)  where k = 2 / (period + 1)
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource, computeEMA } from "./sma";

export const emaIndicator: IndicatorDefinition = {
  id: "ema",
  name: "Exponential Moving Average",
  shortName: "EMA",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 1, max: 500 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
      { label: "HL2", value: "hl2" }, { label: "HLC3", value: "hlc3" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#FF9800" },
  ],
  plots: [
    { key: "ema", label: "EMA", color: "#FF9800", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const emaValues = computeEMA(src, period);
    const data = emaValues
      .map((v, i) => ({ time: candles[i].time, value: v }))
      .filter(d => !isNaN(d.value));
    return [{ key: "ema", data }];
  },
};
